<template>
  <div class="updateLogPage">
    <!-- 面包屑导航：点击“服务支持”时通知父组件返回服务支持首页 -->
    <div class="breadcrumbBar">
      <div class="breadcrumbContent">
        <span class="breadcrumbLink" @click="goBack">{{ $t('menu.service') }}</span>
        <span class="breadcrumbSeparator">></span>
        <span class="breadcrumbCurrent">{{ $t('Service.newsTitle') }}</span>
      </div>
    </div>

    <div class="updateContainer">
      <!-- 顶部分类标签：来自更新日志分类字典，例如 全部 / Summit系列 / Lab系列 -->
      <div class="categoryTabs" v-if="!targetId">
        <div
            class="tabItem"
            v-for="item in getCategoryList"
            :key="item.id"
            :class="{ active: currentCategory === item.id }"
            @click="selectCategory(item.id)"
        >
          {{ language === 'en' && item.fullNameEn ? item.fullNameEn : item.fullName }}
        </div>
      </div>

      <!-- 产品图标列表：展示数据以产品家族表 family_info 为主 -->
      <div
          class="productIcons"
          v-if="!targetId && currentProductList && currentProductList.length > 0"
      >
        <div
            class="productIconItem"
            v-for="item in currentProductList"
            :key="item.id"
            :class="{ active: currentProduct === item.updateLogTypeId }"
            @click="selectProduct(item)"
        >
          <div class="iconWrapper">
            <!-- 优先使用后台产品家族表返回的图标 -->
            <img
                v-if="item.iconUrl"
                :src="item.iconUrl"
                :alt="item.familyCode || item.fullName"
                @error="handleIconError(item)"
            />

            <!-- 图片为空或加载失败时，用产品名称生成文字图标兜底 -->
            <span v-else class="iconFallback">
              {{ getIconText(item.familyCode || item.fullName) }}
            </span>
          </div>

          <!-- 产品名称：优先显示 familyCode，例如 SummitMap / SummitSolid -->
          <div class="iconLabel">
            {{ item.familyCode || item.fullName }}
          </div>
        </div>
      </div>

      <div class="mainContent">
        <!-- 左侧年份导航：默认展示当前年份往前 5 年 -->
        <div class="yearNav" v-if="!targetId">
          <div class="yearNavInner">
            <div
                class="yearItem"
                v-for="year in yearList"
                :key="year"
                :class="{ active: currentYear === year }"
                @click="selectYear(year)"
            >
              {{ year }}年
            </div>
          </div>
        </div>

        <!-- 右侧更新日志列表容器：监听滚动，实现下拉加载更多 -->
        <div
            class="updateListWrapper"
            ref="updateListWrapper"
            @scroll="handleScroll"
        >
          <!-- 首屏加载时显示 loading -->
          <div class="updateList" v-loading="loading && currentPage === 1">
            <div
                class="updateItem"
                v-for="item in allUpdateList"
                :key="item.id"
            >
              <!-- 时间线区域 -->
              <div class="timeline">
                <div class="timelineDot"></div>
              </div>

              <!-- 更新日志正文区域 -->
              <div class="updateContent">
                <div class="updateTitle">{{ item.title }}</div>

                <div class="updateMeta">
                  <span class="updateTime">更新时间：{{ formatDate(item.updateTime || item.createTime) }}</span>
                </div>

                <!-- 后端返回的内容可能是富文本，所以这里使用 v-html -->
                <div class="updateDesc" v-html="item.content"></div>

                <!-- 如果后端返回 improvements 数组，则展示“改进”模块 -->
                <div
                    class="updateSection"
                    v-if="item.improvements && item.improvements.length"
                >
                  <div class="sectionTitle">改进</div>
                  <ul class="sectionList">
                    <li v-for="(imp, idx) in item.improvements" :key="idx">
                      {{ imp }}
                    </li>
                  </ul>
                </div>

                <!-- 如果后端返回 fixes 数组，则展示“修复”模块 -->
                <div
                    class="updateSection"
                    v-if="item.fixes && item.fixes.length"
                >
                  <div class="sectionTitle">修复</div>
                  <ul class="sectionList">
                    <li v-for="(fix, idx) in item.fixes" :key="idx">
                      {{ fix }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- 加载更多 / 没有更多数据状态 -->
          <div class="loadMoreWrapper" v-if="allUpdateList.length > 0">
            <div class="loadingMore" v-if="loadingMore">
              <i class="el-icon-loading"></i>
              <span>加载中...</span>
            </div>
            <div class="noMore" v-else-if="noMoreData">
              <span>没有更多数据了</span>
            </div>
          </div>

          <!-- 空状态：没有日志且不处于加载中时展示 -->
          <div class="emptyState" v-if="allUpdateList.length === 0 && !loading">
            <i class="el-icon-document"></i>
            <p>暂无更新日志</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supportUpdateLog, supportUpdateLogInfo, supportUpdateLogTree } from "@/assets/API/service";
import { family } from "@/assets/API/AllProducts.js";
import define from "@/utils/define";

// 项目接口基础地址，用于拼接后台返回的相对图片路径
const baseUrl = define.APIURl;

export default {
  name: "UpdateLogPage",
  props: {
    targetId: { type: [String, Number], default: "" },
  },

  data() {
    return {
      // 当前语言，用于决定展示中文标题/内容还是英文标题/内容
      language: this.$i18n.locale,

      // loading：首次查询或切换条件时使用
      loading: false,

      // loadingMore：滚动加载下一页时使用
      loadingMore: false,

      // 是否已经没有更多数据
      noMoreData: false,

      // 当前选中的顶部分类。all 是前端补充的“全部”
      currentCategory: "all",

      // 当前选中的产品。注意：这里保存的是更新日志字典产品节点 id，不是 family_info.id
      currentProduct: "",

      // 当前选中的年份，默认当前年
      currentYear: new Date().getFullYear(),

      // 分页参数
      currentPage: 1,
      pageSize: 5,
      total: 0,

      // 顶部分类列表，来自更新日志分类字典
      getCategoryList: [],

      // 产品图标列表，以 family_info 数据为主，再补充 updateLogTypeId 用于查询日志
      productList: [],

      // 当前页面展示的更新日志列表
      allUpdateList: [],
    };
  },

  computed: {
    // 年份列表：当前年份往前推 5 年
    yearList() {
      const currentYear = new Date().getFullYear();
      const years = [];

      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }

      return years;
    },

    // 当前分类下应该显示的产品列表
    currentProductList() {
      // “全部”分类展示所有已匹配到更新日志字典节点的产品
      if (this.currentCategory === "all") {
        return this.productList;
      }

      // 非“全部”分类，只展示属于当前分类的产品
      return this.productList.filter(
          (item) => item.updateLogCategoryId === this.currentCategory
      );
    },
  },

  async mounted() {
    if (this.targetId) await this.fetchTargetUpdate();
    else await this.initUpdateLogPage();
  },

  methods: {
    mapUpdateItem(item) {
      return {
        ...item,
        title: this.language === "zh" ? item.title : (item.titleEn || item.title),
        content: this.language === "zh" ? item.content : (item.contentEn || item.content),
        updateTime: item.updateTime || item.createTime,
      };
    },
    async fetchTargetUpdate() {
      this.loading = true;
      try {
        const res = await supportUpdateLogInfo(this.targetId);
        if (res.code === 200 && res.data) {
          this.allUpdateList = [this.mapUpdateItem(res.data)];
          this.total = 1;
          this.noMoreData = true;
        }
      } finally {
        this.loading = false;
      }
    },
    // 页面初始化：
    // 1. 获取更新日志分类字典，用于分类标签和日志查询 typeId
    // 2. 获取产品家族列表，用于展示产品图标和产品名称
    async initUpdateLogPage() {
      try {
        const [treeRes, familyRes] = await Promise.all([
          supportUpdateLogTree(),
          family({ showSubscribe: "Y" }),
        ]);

        // 将更新日志分类字典里的产品节点整理成 Map
        // key 是产品名，例如 SummitSolid
        // value 中保存字典产品节点 id 和所属分类 id
        const updateLogProductMap = this.buildUpdateLogProductMap(treeRes);

        // 构建顶部分类标签
        this.buildCategoryList(treeRes);

        // 以产品家族表为主构建产品列表，同时补充更新日志查询需要的 updateLogTypeId
        this.buildFamilyProductList(familyRes, updateLogProductMap);

        this.$nextTick(() => {
          const firstProduct = this.currentProductList[0];

          // 默认选中当前分类下第一个产品
          // 这里保存 updateLogTypeId，因为查询更新日志时需要传字典产品节点 id
          this.currentProduct = firstProduct
              ? firstProduct.updateLogTypeId
              : "";

          this.fetchUpdateList();
        });
      } catch (error) {
        console.error("init update log page failed:", error);
        this.$message.error("获取更新日志数据失败");
      }
    },

    // 构建顶部分类标签
    buildCategoryList(treeRes) {
      if (treeRes.code !== 200 || !treeRes.data || !treeRes.data.list) {
        this.getCategoryList = [];
        return;
      }

      // 当前接口里 id 为 0 的节点是更新日志分类根节点
      const rootNode = treeRes.data.list.find((item) => item.id == 0);

      if (!rootNode || !rootNode.children) {
        this.getCategoryList = [];
        return;
      }

      this.getCategoryList = [
        {
          id: "all",
          fullName: "全部",
          fullNameEn: "All",

          // “全部”节点把所有二级分类下的产品节点合并起来
          children: rootNode.children.flatMap((item) => item.children || []),
        },

        // 其他分类直接来自字典树，例如 Summit系列 / Lab系列
        ...rootNode.children.map((item) => ({
          ...item,
          children: item.children || [],
        })),
      ];
    },

    // 将更新日志分类字典中的产品节点整理成 Map
    // 作用：后续用 familyCode 找到对应的更新日志字典产品节点 id
    buildUpdateLogProductMap(treeRes) {
      const map = {};

      if (treeRes.code !== 200 || !treeRes.data || !treeRes.data.list) {
        return map;
      }

      const rootNode = treeRes.data.list.find((item) => item.id == 0);

      if (!rootNode || !rootNode.children) {
        return map;
      }

      rootNode.children.forEach((category) => {
        const children = category.children || [];

        children.forEach((product) => {
          // 用更新日志字典里的产品名称作为匹配 key
          // 例如 product.fullName = SummitSolid
          const key = this.getMatchKey(product.fullName);

          if (!key) return;

          map[key] = {
            // 更新日志查询真正需要的 typeId
            // 对应数据库 support_update_log.s_type_id
            id: product.id,

            // 字典里的产品名称
            fullName: product.fullName,

            // 所属顶部分类
            categoryId: category.id,
            categoryName: category.fullName,
          };
        });
      });

      return map;
    },

    // 以产品家族表为主构建产品图标列表
    // 只有同时存在于 family_info 和更新日志分类字典中的产品，才会显示在当前页面
    buildFamilyProductList(familyRes, updateLogProductMap) {
      if (familyRes.code !== 200 || !Array.isArray(familyRes.data)) {
        this.productList = [];
        return;
      }

      this.productList = familyRes.data
          .map((item) => {
            // 优先用 familyCode 匹配更新日志字典 fullName
            const matchKey = this.getFamilyMatchKey(item);
            const updateLogProduct = updateLogProductMap[matchKey];

            // 如果产品家族表里有，但更新日志分类字典里没有同名产品节点，
            // 则无法拿到 support_update_log.s_type_id 对应的 typeId，所以这里不展示。
            if (!updateLogProduct) {
              return null;
            }

            return {
              ...item,

              // 用字典名称作为当前页产品名，保证和更新日志分类保持一致
              fullName: updateLogProduct.fullName,

              // 图标来自产品家族表
              iconUrl: this.parseImageUrl(item.icon),

              // 查询更新日志时使用的 typeId
              // 注意：这是更新日志分类字典里的产品节点 id，不是产品家族表 id
              updateLogTypeId: updateLogProduct.id,

              // 用于根据顶部分类过滤产品
              updateLogCategoryId: updateLogProduct.categoryId,
              updateLogCategoryName: updateLogProduct.categoryName,
            };
          })
          .filter(Boolean);
    },

    // 获取产品家族表用于匹配字典的名称
    getFamilyMatchKey(item) {
      return this.getMatchKey(
          item.familyCode ||
          item.familyName ||
          item.familyNameEn ||
          item.fullName
      );
    },

    // 匹配 key 只做 trim，不做复杂标准化
    // 因此需要后台保证 familyCode 和更新日志字典 fullName 一致
    getMatchKey(value) {
      return String(value || "").trim();
    },

    formatDate(date) {
      if (!date) return "-";

      const parsed = new Date(date);

      if (Number.isNaN(parsed.getTime())) {
        return "-";
      }

      const pad = (value) => String(value).padStart(2, "0");
      return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
    },

    // 解析图片地址
    // 后台 icon 字段可能是完整 URL，也可能是 JSON 字符串形式的附件数组
    parseImageUrl(value) {
      if (!value) return "";

      try {
        // 完整 URL 直接返回
        if (/^https?:\/\//.test(value)) {
          return value;
        }

        // 常见格式：[{"name":"xxx.png","url":"/api/file/Image/annexpic/xxx.png"}]
        const parsed = JSON.parse(value);
        const url = Array.isArray(parsed) ? parsed[0]?.url : parsed?.url;

        if (!url) return "";

        // 相对路径拼接接口基础地址
        return /^https?:\/\//.test(url) ? url : baseUrl + url;
      } catch (error) {
        return "";
      }
    },

    // 图片加载失败后清空 iconUrl，使模板切换到文字兜底
    handleIconError(item) {
      if (item) {
        item.iconUrl = "";
      }
    },

    // 根据产品名生成兜底图标文字
    getIconText(name) {
      if (!name) return "";

      const upperCaseMatch = name.match(/[A-Z]/g);

      if (upperCaseMatch && upperCaseMatch.length >= 2) {
        return upperCaseMatch.slice(0, 2).join("");
      }

      return name.replace(/\s/g, "").substring(0, 2).toUpperCase();
    },

    // 查询更新日志列表
    fetchUpdateList(isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }

      const params = {
        currentPage: this.currentPage,
        pageSize: this.pageSize,

        // 后端按 typeId 查询日志
        // 这里的 typeId 必须是更新日志分类字典产品节点 id
        typeId: this.currentProduct || null,

        year: this.currentYear || null,
      };

      supportUpdateLog(params)
          .then((res) => {
            if (res.code === 200) {
              this.total = res.data.total || 0;

              const records = res.data.records || [];
              const list = records.map((item) => ({
                ...item,

                // 根据当前语言选择标题和内容
                title: this.language === "zh" ? item.title : item.titleEn,
                content: this.language === "zh" ? item.content : item.contentEn,
                updateTime: item.updateTime || item.createTime,
              }));

              if (isLoadMore) {
                this.allUpdateList = [...this.allUpdateList, ...list];
              } else {
                this.allUpdateList = list;
              }

              // 当前已加载数量达到总数时，标记没有更多数据
              this.noMoreData = this.allUpdateList.length >= this.total;
            } else {
              this.$message.error("获取更新日志失败");
            }
          })
          .finally(() => {
            this.loading = false;
            this.loadingMore = false;
          });
    },

    // 监听右侧列表滚动，接近底部时加载下一页
    handleScroll() {
      const wrapper = this.$refs.updateListWrapper;

      if (!wrapper) return;

      const { scrollTop, scrollHeight, clientHeight } = wrapper;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore();
      }
    },

    // 加载下一页
    loadMore() {
      if (this.loading || this.loadingMore || this.noMoreData) {
        return;
      }

      this.currentPage++;
      this.fetchUpdateList(true);
    },

    // 切换分类、产品、年份时重置分页并重新查询
    resetAndFetch() {
      this.currentPage = 1;
      this.noMoreData = false;
      this.allUpdateList = [];
      this.fetchUpdateList();
    },

    // 切换顶部分类
    selectCategory(categoryId) {
      this.currentCategory = categoryId;

      this.$nextTick(() => {
        const firstProduct = this.currentProductList[0];

        // 切换分类后默认选中该分类下第一个产品
        this.currentProduct = firstProduct
            ? firstProduct.updateLogTypeId
            : "";

        this.resetAndFetch();
      });
    },

    // 切换产品
    selectProduct(product) {
      const typeId = product.updateLogTypeId;

      // 再次点击当前产品时取消选中，此时 typeId 为空，会查询当前年份下全部日志
      this.currentProduct = this.currentProduct === typeId ? "" : typeId;
      this.resetAndFetch();
    },

    // 切换年份
    selectYear(year) {
      this.currentYear = year;
      this.resetAndFetch();
    },

    // 返回服务支持首页
    goBack() {
      this.$emit("back");
    },
  },
};
</script>

<style scoped lang="scss">
/* 页面根容器 */
.updateLogPage {
  background: #fff;
  min-height: 100vh;
}

/* 面包屑黑色横条 */
.breadcrumbBar {
  background: #333;
  padding: 16px 0;

  .breadcrumbContent {
    width: 1200px;
    margin: 0 auto;
    font-size: 14px;
    color: #fff;

    .breadcrumbLink {
      color: #aaa;
      cursor: pointer;

      &:hover {
        color: #fff;
      }
    }

    .breadcrumbSeparator {
      margin: 0 8px;
      color: #666;
    }

    .breadcrumbCurrent {
      color: #fff;
    }
  }
}

/* 页面主体宽度容器 */
.updateContainer {
  width: 1200px;
  margin: 0 auto;
  padding: 40px 0;
}

/* 顶部分类标签 */
.categoryTabs {
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 30px;
  background: #f0f0f0;
  border-radius: 30px;
  padding: 4px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;

  .tabItem {
    padding: 10px 28px;
    border-radius: 24px;
    font-size: 15px;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;

    &:hover {
      color: #409eff;
    }

    &.active {
      color: #fff;
      background: #409eff;
    }
  }
}

/* 产品图标列表区域 */
.productIcons {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 42px;
  margin-bottom: 42px;
  padding: 24px 0 30px;
  border-bottom: 1px solid #e5e5e5;

  .productIconItem {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 88px;
    cursor: pointer;
    color: #333;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      color: #409eff;
    }

    &.active {
      .iconWrapper {
        transform: scale(1.15);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
      }
    }

    .iconWrapper {
      width: 56px;
      height: 56px;
      border-radius: 12px;
      border: 1px solid transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 10px;
      background: #fff;
      overflow: hidden;
      transition: all 0.2s ease;

      img {
        width: 56px;
        height: 56px;
        object-fit: contain;
        display: block;
      }

      .iconFallback {
        width: 56px;
        height: 56px;
        border-radius: 12px;
        background: #409eff;
        color: #fff;
        font-size: 24px;
        font-weight: 700;
        line-height: 56px;
        text-align: center;
      }
    }

    .iconLabel {
      font-size: 14px;
      line-height: 20px;
      text-align: center;
      white-space: nowrap;
    }
  }
}

/* 内容区域：左侧年份 + 右侧日志 */
.mainContent {
  display: flex;
  align-items: flex-start;
}

/* 左侧年份导航 */
.yearNav {
  width: 140px;
  flex-shrink: 0;
  padding-top: 10px;

  .yearNavInner {
    position: sticky;
    top: 20px;
  }

  .yearItem {
    height: 42px;
    line-height: 42px;
    padding-left: 18px;
    font-size: 15px;
    color: #666;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: all 0.2s ease;

    &:hover {
      color: #409eff;
    }

    &.active {
      color: #409eff;
      border-left-color: #409eff;
      background: #f3f8ff;
      font-weight: 600;
    }
  }
}

/* 右侧日志滚动容器 */
.updateListWrapper {
  flex: 1;
  max-height: 720px;
  overflow-y: auto;
  padding-left: 34px;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d8d8d8;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.updateList {
  min-height: 200px;
}

/* 单条更新日志 */
.updateItem {
  display: flex;
  position: relative;
  padding-bottom: 34px;

  .timeline {
    width: 150px;
    flex-shrink: 0;
    position: relative;
    padding-top: 2px;

    &::after {
      content: "";
      position: absolute;
      left: 6px;
      top: 16px;
      bottom: -34px;
      width: 1px;
      background: #e5e5e5;
    }

    .timelineDot {
      position: absolute;
      left: 0;
      top: 7px;
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background: #409eff;

      .updateMeta {
        margin-top: 8px;
        margin-bottom: 10px;
        display: flex;
        align-items: center;

        .updateTime {
          font-size: 13px;
          color: #8a97a5;
          line-height: 1.4;
        }
      }
      z-index: 1;
    }


  }

  .updateContent {
    flex: 1;
    padding-bottom: 4px;

    .updateTitle {
      font-size: 18px;
      line-height: 28px;
      color: #222;
      font-weight: 600;
      margin-bottom: 12px;
    }

    .updateDesc {
      color: #555;
      font-size: 14px;
      line-height: 24px;
      margin-bottom: 14px;
    }

    .updateSection {
      margin-top: 12px;

      .sectionTitle {
        font-size: 15px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
      }

      .sectionList {
        margin: 0;
        padding-left: 18px;
        color: #666;
        font-size: 14px;
        line-height: 24px;

        li {
          margin-bottom: 4px;
        }
      }
    }
  }
}

/* 底部加载更多状态 */
.loadMoreWrapper {
  text-align: center;
  color: #999;
  font-size: 14px;
  padding: 16px 0 6px;

  .loadingMore {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

/* 空数据状态 */
.emptyState {
  padding: 80px 0;
  text-align: center;
  color: #999;

  i {
    font-size: 42px;
    margin-bottom: 12px;
    color: #c0c4cc;
  }

  p {
    margin: 0;
    font-size: 15px;
  }
}

/* 小屏宽度适配 */
@media screen and (max-width: 1200px) {
  .breadcrumbBar .breadcrumbContent,
  .updateContainer {
    width: calc(100% - 40px);
  }
}
</style>
